import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  Badge,
  Avatar,
  Modal,
  message,
  Tooltip,
  Statistic
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  FilterOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  UserAddOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  ReloadOutlined,
  VideoCameraOutlined,
  SettingOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { GET_APPOINTMENTS, GET_APPOINTMENT_STATS, GET_PROVIDERS, GET_DOCTOR_APPOINTMENTS } from '../graphql/queries';
import { CANCEL_APPOINTMENT } from '../graphql/mutations';
import { useTranslation } from '../hooks/useTranslation';
import { useGraphQLError } from '../hooks/useGraphQL';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import AppointmentForm from '../components/AppointmentForm';
import ProviderSelectionModal from '../components/ProviderSelectionModal';
import AppointmentStatusModal from '../components/AppointmentStatusModal';
import AppointmentReportExport from '../components/AppointmentReportExport';
import { useAuth } from '../hooks/useAuth';
import { useAppointmentSSESubscription, AppointmentEvent, useAppointmentSSE } from '../lib/sse';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
interface Appointment {
  id: string;
  app_user_id: string;
  patient_id?: string;
  provider_id: string;
  date: string;
  time: string;
  expected_time_range?: string;
  duration: number;
  status: string;
  queue_number?: number;
  type: string;
  reservation_type?: string;
  chief_complaint?: string;
  symptoms?: string[];
  appointment_notes?: string;
  internal_remark?: string;
  priority: string;
  consultation_type: string;

  payment_status: string;
  payment_method?: string;
  total_amount?: number;
  patient_phone?: string;
  patient_email?: string;
  timezone?: string;
  created_at: string;
  updated_at: string;
  confirmed_at?: string;
  started_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  cancelled_reason?: string;
  cancelled_by?: string;
  otherStatuses?: {
    doctorJoinStatus?: { status: 'pending' | 'joined' | 'ended'; timestamp: string };
    operatorJoinStatus?: { status: 'pending' | 'joined' | 'ended'; timestamp: string };
    patientJoinStatus?: { status: 'pending' | 'joined' | 'ended'; timestamp: string };
  };
  app_user?: {
    id: string;
    firstname?: string;
    lastname?: string;
    email?: string;
    phone_number?: string;
    tenant?: string;
  };
  patient?: {
    id: string;
    first_name_en: string;
    last_name_en: string;
    first_name_zh_hk: string;
    last_name_zh_hk: string;
    gender: string;
    birthdate?: string;
    email?: string;
    phone_number?: string;
    status: string;
    relationship_to_owner?: string;
  };
}

interface AppointmentStats {
  scheduled?: number;
  confirmed?: number;
  in_progress?: number;
  completed?: number;
  cancelled?: number;
  no_show?: number;
}


const Appointments: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleError } = useGraphQLError();
  const { user } = useAuth();

  // Helper function for timezone-aware date comparison
  const getDateKeyInTimeZone = (dateValue: string | Date, timeZone: string) => {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find(p => p.type === 'year')?.value ?? '';
    const month = parts.find(p => p.type === 'month')?.value ?? '';
    const day = parts.find(p => p.type === 'day')?.value ?? '';
    return `${year}-${month}-${day}`;
  };

  // Initialize state with localStorage values or defaults
  const [searchText, setSearchText] = useState(() => {
    return localStorage.getItem('appointment_searchText') || '';
  });
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
  const [searchOverrideFilters, setSearchOverrideFilters] = useState(() => {
    return localStorage.getItem('appointment_searchOverrideFilters') === 'true';
  });
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const defaultStatuses = ['pending', 'scheduled', 'confirmed', 'in_progress', 'consulted', 'cancelled', 'no_show', 'ready', 'delivering'];
    const saved = localStorage.getItem('appointment_statusFilter');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (!Array.isArray(parsed) || parsed.length === 0) {
          return defaultStatuses;
        }
        // If 'all' is selected, return all statuses
        if (parsed.includes('all')) {
          return ['all'];
        }
        // Ensure 'completed' is hidden by default if someone saved only 'all'
        return parsed.filter((s: string) => s !== 'completed');
      } catch {
        return defaultStatuses;
      }
    }
    return defaultStatuses;
  });

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(() => {
    const saved = localStorage.getItem('appointment_dateRange');
    if (saved) {
      return JSON.parse(saved).map((date: string) => dayjs(date));
    }
    // Use Hong Kong timezone for default date range
    const todayKey = getDateKeyInTimeZone(new Date(), 'Asia/Hong_Kong');
    const todayStart = dayjs(todayKey);
    const todayEnd = dayjs(todayKey);
    return [todayStart, todayEnd];
  });
  const [isNewAppointmentModalVisible, setIsNewAppointmentModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assigningAppointmentId, setAssigningAppointmentId] = useState<string | null>(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedAppointmentForStatus, setSelectedAppointmentForStatus] = useState<Appointment | null>(null);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('appointment_searchText', searchText);
  }, [searchText]);

  useEffect(() => {
    localStorage.setItem('appointment_searchOverrideFilters', searchOverrideFilters.toString());
  }, [searchOverrideFilters]);

  // Debounce search text for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    localStorage.setItem('appointment_statusFilter', JSON.stringify(statusFilter));
  }, [statusFilter]);



  useEffect(() => {
    if (dateRange) {
      localStorage.setItem('appointment_dateRange', JSON.stringify([
        dateRange[0].toISOString(),
        dateRange[1].toISOString()
      ]));
    } else {
      localStorage.removeItem('appointment_dateRange');
    }
  }, [dateRange]);

  // Create filter object for GraphQL query
  const filterParams = useMemo(() => {
    const filter: any = {};

    if (debouncedSearchText) {
      filter.searchText = debouncedSearchText;
      filter.searchOverrideFilters = searchOverrideFilters;
    }

    if (statusFilter.length > 0 && !statusFilter.includes('all')) {
      filter.statusFilter = statusFilter;
    }

    if (dateRange) {
      filter.startDate = dateRange[0].format('YYYY-MM-DD');
      filter.endDate = dateRange[1].format('YYYY-MM-DD');
    }

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [debouncedSearchText, searchOverrideFilters, statusFilter, dateRange]);

  // GraphQL queries - handle role-based data fetching
  const { data: appointmentsData, loading: appointmentsLoading, error: appointmentsError, refetch: refetchAppointments } = useQuery(
    user?.doctor_id ? GET_DOCTOR_APPOINTMENTS : GET_APPOINTMENTS,
    {
      variables: user?.doctor_id ? { doctorId: user.doctor_id, filter: filterParams } : { filter: filterParams },
      skip: !user
    }
  );
  const { data: statsData, loading: statsLoading, error: statsError /* refetch: refetchStats */ } = useQuery(GET_APPOINTMENT_STATS);
  const { data: providersData, error: providersError } = useQuery(GET_PROVIDERS);

  const [cancelAppointmentMutation] = useMutation(CANCEL_APPOINTMENT);

  // State to track appointments for real-time updates
  const [appointmentsState, setAppointmentsState] = useState<Appointment[]>([]);

  // Update appointments when data is loaded
  useEffect(() => {
    if (appointmentsData) {
      const newAppointments = appointmentsData.appointments || appointmentsData.doctorAppointments || [];
      setAppointmentsState(newAppointments);
    }
  }, [appointmentsData]);

  // Function to update a specific appointment
  const updateAppointment = useCallback((appointmentId: string, updatedData: Partial<Appointment>) => {
    setAppointmentsState(prevAppointments =>
      prevAppointments.map(appointment =>
        appointment.id === appointmentId
          ? { ...appointment, ...updatedData }
          : appointment
      )
    );
  }, []);

  // Handle GraphQL errors with toast notifications
  useEffect(() => {
    if (appointmentsError) {
      message.error({
        content: `${t('appointments.errorLoadingAppointments')}: ${handleError(appointmentsError)}`,
        duration: 6,
      });
    }
  }, [appointmentsError, handleError, t]);

  useEffect(() => {
    if (statsError) {
      message.error({
        content: `${t('appointments.errorLoadingStats')}: ${handleError(statsError)}`,
        duration: 6,
      });
    }
  }, [statsError, handleError, t]);

  useEffect(() => {
    if (providersError) {
      message.error({
        content: `${t('appointments.errorLoadingProviders')}: ${handleError(providersError)}`,
        duration: 6,
      });
    }
  }, [providersError, handleError, t]);

  // SSE subscription for real-time appointment updates
  const handleAppointmentEvent = useCallback((event: AppointmentEvent) => {
    console.log('Appointment SSE event received:', event);

    if (event.appointmentId && event.data) {
      if (event.type === 'appointment_updated') {
        // Update specific appointment row
        console.log('Updating specific appointment via SSE:', event.appointmentId);
        updateAppointment(event.appointmentId, event.data);
      } else if (event.type === 'appointment.join_status.updated') {
        // Update join status for specific appointment
        console.log('Updating join status via SSE:', event.appointmentId);
        updateAppointment(event.appointmentId, { otherStatuses: event.data.otherStatuses });
      } else if (event.type === 'appointment_status_changed' ||
        event.type === 'appointment_confirmed' ||
        event.type === 'appointment_started' ||
        event.type === 'appointment_completed' ||
        event.type === 'appointment_cancelled') {
        // For status changes, update the appointment with new status
        console.log('Updating appointment status via SSE:', event.appointmentId);
        updateAppointment(event.appointmentId, event.data);
      }
    } else {
      // Fallback: refetch all data if no specific appointment ID
      console.log('No appointment ID in event, refetching all data...');
      refetchAppointments();
    }
  }, [updateAppointment, refetchAppointments]);

  // Subscribe to SSE events
  useAppointmentSSESubscription(handleAppointmentEvent);

  // Use state for appointments to enable real-time updates
  const appointments: Appointment[] = appointmentsState;

  const stats: AppointmentStats = statsData?.appointmentStats || {
    scheduled: 0,
    confirmed: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
    no_show: 0
  };

  const providers = providersData?.providers || [];

  // For today's appointments calculation, we need to get all appointments (not filtered)
  // This is used for the quick filter buttons and provider queues
  const { data: allAppointmentsData } = useQuery(GET_APPOINTMENTS, {
    variables: {
      filter: {
        startDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      }
    },
    skip: !user || !!user?.doctor_id // Skip for doctors as they have their own query
  });

  const allAppointments = allAppointmentsData?.appointments || [];
  const appointmentsToday = allAppointments.filter((a: Appointment) => {
    const tz = a.timezone || 'Asia/Hong_Kong';
    const apptDateKey = getDateKeyInTimeZone(a.date, tz);
    const todayKey = getDateKeyInTimeZone(new Date(), tz);
    return apptDateKey === todayKey;
  });

  const pendingQueueLength = appointmentsToday.filter((a: Appointment) => a.status === 'pending').length;

  const providerQueues = providers
    .map((p: any) => {
      const forProvider = appointmentsToday.filter((a: Appointment) => a.provider_id === p.id);
      const confirmed = forProvider.filter((a: Appointment) => a.status === 'confirmed').length;
      const ready = forProvider.filter((a: Appointment) => a.status === 'ready').length;
      const consulting = forProvider.filter((a: Appointment) => a.status === 'in_progress' || a.status === 'consulting').length;

      const name = `${p.doctor?.first_name_en ?? ''} ${p.doctor?.last_name_en ?? ''}`.trim() || p.id;
      return { id: p.id, name, confirmed, ready, consulting, total: confirmed + ready + consulting };
    })
    .filter((q: any) => q.total > 0)
    .sort((a: any, b: any) => b.total - a.total);

  // Use appointments directly from backend (already filtered)
  const orderedAppointments = appointments;


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'gold';
      case 'scheduled': return 'blue';
      case 'confirmed': return 'green';
      case 'in_progress': return 'orange';
      case 'consulted': return 'cyan';
      case 'completed': return 'success';
      case 'cancelled': return 'red';
      case 'no_show': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <ClockCircleOutlined />;
      case 'scheduled': return <ClockCircleOutlined />;
      case 'confirmed': return <CheckCircleOutlined />;
      case 'in_progress': return <SyncOutlined />;
      case 'consulted': return <CheckCircleOutlined />;
      case 'completed': return <CheckCircleOutlined />;
      case 'cancelled': return <CloseCircleOutlined />;
      case 'no_show': return <ExclamationCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const getJoinStatusDisplay = (appointment: Appointment) => {
    if (!appointment.otherStatuses) return null;

    const { doctorJoinStatus, operatorJoinStatus, patientJoinStatus } = appointment.otherStatuses;

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'joined': return 'success';
        case 'pending': return 'warning';
        case 'ended': return 'default';
        default: return 'default';
      }
    };

    const getStatusIcon = (status: string) => {
      switch (status) {
        case 'joined': return <CheckCircleOutlined />;
        case 'pending': return <ClockCircleOutlined />;
        case 'ended': return <CloseCircleOutlined />;
        default: return <ClockCircleOutlined />;
      }
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {doctorJoinStatus && (
          <Tag color={getStatusColor(doctorJoinStatus.status)} icon={getStatusIcon(doctorJoinStatus.status)}>
            Dr: {t(`appointments.joinStatuses.${doctorJoinStatus.status}`)}
          </Tag>
        )}
        {operatorJoinStatus && (
          <Tag color={getStatusColor(operatorJoinStatus.status)} icon={getStatusIcon(operatorJoinStatus.status)}>
            Op: {t(`appointments.joinStatuses.${operatorJoinStatus.status}`)}
          </Tag>
        )}
        {patientJoinStatus && (
          <Tag color={getStatusColor(patientJoinStatus.status)} icon={getStatusIcon(patientJoinStatus.status)}>
            Pt: {t(`appointments.joinStatuses.${patientJoinStatus.status}`)}
          </Tag>
        )}
        {!doctorJoinStatus && !operatorJoinStatus && !patientJoinStatus && (
          <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>
        )}
      </div>
    );
  };



  const handleViewAppointment = (appointment: Appointment) => {
    navigate(`/appointments/${appointment.id}`);
  };
  const handleCancelAppointment = (appointment: Appointment) => {
    let reasonValue = '';

    Modal.confirm({
      title: t('appointments.cancelAppointment'),
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>
            {t('appointments.cancelConfirm', { reference: appointment.id.slice(-6) })}
          </div>
          <Input
            placeholder={t('appointments.cancellationReason')}
            onChange={(e) => { reasonValue = e.target.value; }}
          />
        </div>
      ),
      okText: t('appointments.cancelAppointment'),
      okType: 'danger',
      cancelText: t('common.no'),
      onOk: async () => {
        try {
          await cancelAppointmentMutation({
            variables: {
              id: appointment.id,
              reason: reasonValue || 'Cancelled by admin',
              cancelledBy: user?.email || user?.id || 'admin',
            },
            refetchQueries: [
              { query: GET_APPOINTMENTS },
              { query: GET_APPOINTMENT_STATS },
            ],
            awaitRefetchQueries: true,
          });
          message.success(t('appointments.appointmentCancelled'));
        } catch (err) {
          message.error({ content: handleError(err), duration: 6 });
        }
      }
    });
  };

  const handleStartVideoCall = (appointment: Appointment) => {
    // Navigate to the Agora call page
    navigate(`/agora-call/${appointment.id}`);
  };

  const handleCreateCallLink = (appointment: Appointment) => {
    // For now, we'll navigate to the Agora call page which will create the link
    // In a real implementation, you might want to call a mutation to create the Agora link first
    navigate(`/agora-call/${appointment.id}`);
  };

  const handleWhatsApp = (appointment: Appointment) => {
    // Get phone number from app_user first (verified phone number), then fall back to patient
    const phoneNumber = appointment.app_user?.phone_number ||
      appointment.patient_phone ||
      appointment.patient?.phone_number;

    if (!phoneNumber) {
      message.warning(t('appointments.noPhoneNumber'));
      return;
    }

    // Extract numeric digits for WhatsApp link
    const whatsappNumber = phoneNumber.replace(/[^0-9]/g, '');
    const whatsappUrl = `https://wa.me/${whatsappNumber}`;

    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSearchOverrideFilters(false);
    setStatusFilter(['all']);
    setDateRange(null);
  };

  const handleNewAppointmentSuccess = () => {
    refetchAppointments();
  };

  const openAssignProvider = (appointment: Appointment) => {
    setAssigningAppointmentId(appointment.id);
    setAssignModalVisible(true);
  };

  const openStatusModal = (appointment: Appointment) => {
    setSelectedAppointmentForStatus(appointment);
    setStatusModalVisible(true);
  };

  const columns = [
    {
      title: t('appointments.reference'),
      key: 'reference',
      width: 70,
      render: (record: Appointment) => (
        <Text code style={{ fontSize: '12px' }}>{record.id.slice(-6)}</Text>
      )
    },
    {
      title: t('appointments.dateTime'),
      key: 'datetime',
      width: 90,
      render: (record: Appointment) => (
        <div>
          <div style={{ fontWeight: 500 }}>{dayjs(record.date).format('YYYY/MM/DD')}</div>
          <Text type="secondary">{record.time}</Text>
          {record.status === 'pending' && record.expected_time_range && (
            <div style={{ marginTop: 2 }}>
              <Tag color="orange">
                {t('appointments.expectedTimeRange')}: {record.expected_time_range}
              </Tag>
            </div>
          )}
        </div>
      )
    },
    {
      title: t('appointments.reservationType'),
      key: 'reservation_type',
      width: 60,
      render: (record: Appointment) => (
        <Space size={4} wrap>
          <Tag color={record.reservation_type === 'appointment' ? 'blue' : 'green'}>
            {record.reservation_type ? t(`appointments.reservationTypes.${record.reservation_type}`) : t('common.na')}
          </Tag>
          {record.type === 'body_check' && (
            <Tag color="orange">{t('appointments.types.body_check')}</Tag>
          )}
        </Space>
      )
    },
    {
      title: t('appointments.patient'),
      key: 'patient',
      width: 180,
      render: (record: Appointment) => {
        const patient = record.patient;
        const appUser = record.app_user;

        // Use patient information if available, otherwise fall back to app user
        const displayName = patient
          ? `${patient.last_name_en || ''} ${patient.first_name_en || ''}`.trim() ||
          `${patient.first_name_zh_hk || ''} ${patient.last_name_zh_hk || ''}`.trim()
          : appUser?.firstname || appUser?.lastname
            ? `${appUser?.firstname ?? ''} ${appUser?.lastname ?? ''}`.trim()
            : t('common.na');

        const contactInfo = appUser?.phone_number || appUser?.email ||
          record.patient_phone || patient?.phone_number ||
          record.patient_email || patient?.email ||
          t('common.na');

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar icon={<UserOutlined />} size="small" />
            <div style={{ marginLeft: 8, display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 500 }}>
                {displayName}
              </span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(`common.tenants.${record.app_user?.tenant}`)}
              </Text> 
              <Text type="secondary" style={{ fontSize: 12 }}>
                {contactInfo}
              </Text>
              {patient && (
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {t(`patients.relationships.${patient.relationship_to_owner || 'self'}`)}
                </Text>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Badge
          status={getStatusColor(status) as any}
          text={
            <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
              {t(`appointments.statuses.${status}`)}
            </Tag>
          }
        />
      )
    },
    {
      title: t('appointments.queue'),
      key: 'queue_number',
      width: 70,
      render: (record: Appointment) => (
        ['in_progress', 'ready', 'confirmed'].includes(record.status) ? <Text>{record.queue_number ?? 0}</Text> : <Text type="secondary">-</Text>
      )
    },
    // {
    //   title: t('appointments.payment'),
    //   key: 'payment',
    //   width: 120,
    //   render: (record: Appointment) => (
    //     <div>
    //       <div style={{ fontWeight: 500 }}>
    //         {t(`appointments.paymentStatuses.${record.payment_status}`)}
    //       </div>
    //       {record.total_amount && (
    //         <Text type="secondary">${record.total_amount}</Text>
    //       )}
    //     </div>
    //   )
    // },
    {
      title: t('appointments.joinStatus'),
      key: 'joinStatus',
      width: 140,
      render: (record: Appointment) => getJoinStatusDisplay(record)
    },
    {
      title: t('appointments.internalRemark'),
      key: 'internalRemark',
      width: 50,
      render: (record: Appointment) => (
        <div style={{ maxWidth: 200 }}>
          {record.internal_remark ? (
            <Tooltip 
              title={
                <div style={{ whiteSpace: 'pre-wrap', maxWidth: 300 }}>
                  {record.internal_remark}
                </div>
              }
            >
              <Text 
                ellipsis={{ tooltip: false }}
                style={{ 
                  display: 'block',
                  maxWidth: 200,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {record.internal_remark}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>
          )}
        </div>
      )
    },
    {
      title: t('appointments.provider'),
      key: 'assignProvider',
      width: 140,
      render: (record: Appointment) => {
        const provider = providers.find((p: any) => p.id === record.provider_id);
        const providerName = provider ? `${provider.doctor?.first_name_en ?? ''} ${provider.doctor?.last_name_en ?? ''}`.trim() || provider.id : null;
        return (
          <div>
            {(!record.provider_id && record.status === 'pending' && !user?.doctor_id) ? (
              <Button type="primary" size="small" onClick={(e) => {
                e.stopPropagation();
                openAssignProvider(record);
              }}>
                {t('appointments.assignProvider')}
              </Button>
            ) : providerName ? (
              <Text>{providerName}</Text>
            ) : (
              <Text type="secondary">{t('appointments.assigned')}</Text>
            )}
          </div>
        );
      }
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      render: (record: Appointment) => {
        const canStartCall = ['confirmed', 'in_progress'].includes(record.status);
        const canCreateLink = record.status === 'scheduled';
        const showCall = canStartCall || canCreateLink;
        const callTooltip = canStartCall
          ? t('appointments.startVideoCall')
          : t('appointments.createCallLink');
        const handleCall = () => (canStartCall ? handleStartVideoCall(record) : handleCreateCallLink(record));

        return (
          <Space size="small">
            <Tooltip title={t('appointments.view')}>
              <Button type="text" icon={<EyeOutlined />} onClick={(e) => {
                e.stopPropagation();
                handleViewAppointment(record);
              }} />
            </Tooltip>
            {/* {(!record.provider_id && record.status === 'pending' && !user?.doctor_id) && (
              <Tooltip title={t('appointments.assignProvider')}>
                <Button type="primary" size="small" icon={<UserAddOutlined />} onClick={(e) => {
                  e.stopPropagation();
                  openAssignProvider(record);
                }} />
              </Tooltip>
            )} */}
            {['pending', 'confirmed', 'scheduled', 'in_progress'].includes(record.status) && (
              <Tooltip title={t('appointments.cancel')}>
                <Button type="text" danger icon={<CloseCircleOutlined />} onClick={(e) => {
                  e.stopPropagation();
                  handleCancelAppointment(record);
                }} />
              </Tooltip>
            )}
            {showCall && (
              <Tooltip title={callTooltip}>
                <Button type="text" icon={<VideoCameraOutlined />} onClick={(e) => {
                  e.stopPropagation();
                  handleCall();
                }} />
              </Tooltip>
            )}
            <Tooltip title={t('appointments.contactWhatsApp')}>
              <Button
                type="text"
                icon={<MessageOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleWhatsApp(record);
                }}
                style={{ color: '#25D366' }}
              />
            </Tooltip>
            {/* <Tooltip title={t('common.delete')}>
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteAppointment(record)} />
            </Tooltip> */}
          </Space>
        );
      }
    }
  ];

  const statsCards = [
    {
      title: t('appointments.statuses.scheduled'),
      value: stats.scheduled || 0,
      icon: <CalendarOutlined />,
      color: '#1890ff'
    },
    {
      title: t('appointments.statuses.confirmed'),
      value: stats.confirmed || 0,
      icon: <CheckCircleOutlined />,
      color: '#52c41a'
    },
    {
      title: t('appointments.statuses.in_progress'),
      value: stats.in_progress || 0,
      icon: <SyncOutlined />,
      color: '#fa8c16'
    },
    {
      title: t('appointments.statuses.completed'),
      value: stats.completed || 0,
      icon: <CheckCircleOutlined />,
      color: '#722ed1'
    },
    {
      title: t('appointments.statuses.cancelled'),
      value: stats.cancelled || 0,
      icon: <CloseCircleOutlined />,
      color: '#ff4d4f'
    },
    {
      title: t('appointments.statuses.no_show'),
      value: stats.no_show || 0,
      icon: <ExclamationCircleOutlined />,
      color: '#8c8c8c'
    }
  ];

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            {user?.doctor_id ? t('appointments.myAppointments') : t('appointments.title')}
          </Title>
          <Text type="secondary">
            {user?.doctor_id ? t('appointments.myAppointmentsSubtitle') : t('appointments.subtitle')}
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetchAppointments()}
              loading={appointmentsLoading}
            >
              {t('common.refresh')}
            </Button>
            <AppointmentReportExport />
            {/* {!user?.doctor_id && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsNewAppointmentModalVisible(true)}>
                {t('appointments.newAppointment')}
              </Button>
            )} */}
          </Space>
        </Col>
      </Row>

      {/* Stats Cards */}
      {/* <Row gutter={[16, 16]} style={{ marginBottom: 24 }}> */}
      {/* {statsCards.map((stat, index) => (
          <Col xs={12} sm={8} md={4} key={index}>
            <Card>
              <Statistic
                title={stat.title}
                value={stat.value}
                prefix={<span style={{ color: stat.color }}>{stat.icon}</span>}
                valueStyle={{ fontSize: '20px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        ))} */}
      {/* <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title={t('appointments.pendingQueue')}
              value={pendingQueueLength}
              prefix={<span style={{ color: '#fa541c' }}><ClockCircleOutlined /></span>}
              valueStyle={{ fontSize: '20px', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row> */}

      {/* Quick Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[8, 8]}>
          <Col>
            <Text strong style={{ marginRight: 16 }}>{t('appointments.quickFilters')}</Text>
          </Col>
          <Col>
            <Button
              size="small"
              type={statusFilter.includes('pending') && statusFilter.length === 1 && dateRange ? 'primary' : 'default'}
              onClick={() => {
                setStatusFilter(['pending']);
                const todayKey = getDateKeyInTimeZone(new Date(), 'Asia/Hong_Kong');
                const todayStart = dayjs(todayKey).add(-1, 'day');
                const todayEnd = dayjs(todayKey);
                setDateRange([todayStart, todayEnd]);
              }}
            >
              {t('appointments.statuses.pending')} ({appointmentsToday.filter((a: Appointment) => a.status === 'pending').length})
            </Button>
          </Col>
          <Col>
            <Button
              size="small"
              type={statusFilter.includes('confirmed') && statusFilter.length === 1 && dateRange ? 'primary' : 'default'}
              onClick={() => {
                setStatusFilter(['confirmed']);
                const todayKey = getDateKeyInTimeZone(new Date(), 'Asia/Hong_Kong');
                const todayStart = dayjs(todayKey);
                const todayEnd = dayjs(todayKey);
                setDateRange([todayStart, todayEnd]);
              }}
            >
              {t('appointments.statuses.confirmed')} ({appointmentsToday.filter((a: Appointment) => a.status === 'confirmed').length})
            </Button>
          </Col>
          <Col>
            <Button
              size="small"
              type={statusFilter.includes('in_progress') && statusFilter.length === 1 && dateRange ? 'primary' : 'default'}
              onClick={() => {
                setStatusFilter(['in_progress']);
                const todayKey = getDateKeyInTimeZone(new Date(), 'Asia/Hong_Kong');
                const todayStart = dayjs(todayKey);
                const todayEnd = dayjs(todayKey);
                setDateRange([todayStart, todayEnd]);
              }}
            >
              {t('appointments.statuses.in_progress')} ({appointmentsToday.filter((a: Appointment) => a.status === 'in_progress').length})
            </Button>
          </Col>
          <Col>
            <Button
              size="small"
              type={statusFilter.includes('consulted') && statusFilter.length === 1 && dateRange ? 'primary' : 'default'}
              onClick={() => {
                setStatusFilter(['consulted']);
                const todayKey = getDateKeyInTimeZone(new Date(), 'Asia/Hong_Kong');
                const todayStart = dayjs(todayKey);
                const todayEnd = dayjs(todayKey);
                setDateRange([todayStart, todayEnd]);
              }}
            >
              {t('appointments.statuses.consulted')} ({appointmentsToday.filter((a: Appointment) => a.status === 'consulted').length})
            </Button>
          </Col>
          <Col>
            <Button
              size="small"
              type={statusFilter.includes('completed') && statusFilter.length === 1 && dateRange ? 'primary' : 'default'}
              onClick={() => {
                setStatusFilter(['completed']);
                const todayKey = getDateKeyInTimeZone(new Date(), 'Asia/Hong_Kong');
                const todayStart = dayjs(todayKey);
                const todayEnd = dayjs(todayKey);
                setDateRange([todayStart, todayEnd]);
              }}
            >
              {t('appointments.statuses.completed')} ({appointmentsToday.filter((a: Appointment) => a.status === 'completed').length})
            </Button>
          </Col>
          <Col>
            <Button
              size="small"
              type={statusFilter.includes('all') && dateRange ? 'primary' : 'default'}
              onClick={() => {
                setStatusFilter(['all']);
                const todayKey = getDateKeyInTimeZone(new Date(), 'Asia/Hong_Kong');
                const todayStart = dayjs(todayKey);
                const todayEnd = dayjs(todayKey);
                setDateRange([todayStart, todayEnd]);
              }}
            >
              {t('appointments.statuses.all')} ({appointmentsToday.length})
            </Button>
          </Col>
        </Row>
        <Row style={{ marginTop: 8 }}>
          <Col>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('appointments.quickFiltersHint')}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <div>
              <Search
                placeholder={t('appointments.searchAppointments')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={() => setDebouncedSearchText(searchText)}
                prefix={<SearchOutlined />}
                loading={searchText !== debouncedSearchText}
              />
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="checkbox"
                    checked={searchOverrideFilters}
                    onChange={(e) => setSearchOverrideFilters(e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {t('appointments.searchOverrideFilters')}
                  </Text>
                </label>
              </div>
              <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {searchOverrideFilters
                  ? t('appointments.searchHintOverride')
                  : t('appointments.searchHintCombined')
                }
              </Text>
            </div>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Select
              mode="multiple"
              placeholder={t('common.status')}
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              maxTagCount="responsive"
            >
              <Option value="all">{t('appointments.allStatus')}</Option>
              <Option value="pending">{t('appointments.statuses.pending')}</Option>
              <Option value="scheduled">{t('appointments.statuses.scheduled')}</Option>
              <Option value="confirmed">{t('appointments.statuses.confirmed')}</Option>
              <Option value="ready">{t('appointments.statuses.ready')}</Option>
              <Option value="in_progress">{t('appointments.statuses.in_progress')}</Option>
              <Option value="consulted">{t('appointments.statuses.consulted')}</Option>
              <Option value="completed">{t('appointments.statuses.completed')}</Option>
              <Option value="cancelled">{t('appointments.statuses.cancelled')}</Option>
              <Option value="no_show">{t('appointments.statuses.no_show')}</Option>
              <Option value="delivering">{t('appointments.statuses.delivering')}</Option>
            </Select>
          </Col>

          <Col xs={12} sm={6} md={6}>
            <Space>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                style={{ width: '100%' }}
                placeholder={[t('appointments.startDate'), t('appointments.endDate')]}
              />
              <Button
                onClick={() => {
                  const todayKey = getDateKeyInTimeZone(new Date(), 'Asia/Hong_Kong');
                  const todayStart = dayjs(todayKey);
                  const todayEnd = dayjs(todayKey);
                  setDateRange([todayStart, todayEnd]);
                }}
              >
                {t('dashboard.today')}
              </Button>
            </Space>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Button
              icon={<FilterOutlined />}
              onClick={handleClearFilters}
              style={{ width: '100%' }}
            >
              {t('appointments.clearFilters')}
            </Button>
          </Col>
        </Row>

        {/* Filter Summary */}
        {(searchText || (statusFilter.length > 0 && !statusFilter.includes('all')) || dateRange) && (
          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Text type="secondary">
                {t('appointments.activeFilters')}
                {searchText && (
                  <Tag closable onClose={() => setSearchText('')} color="blue">
                    Search: "{searchText}" {searchText.length <= 6 && 'ID search'}
                    {searchOverrideFilters ? ' (overrides filters)' : ' (combines with filters)'}
                  </Tag>
                )}
                {statusFilter.length > 0 && !statusFilter.includes('all') && (!searchText || !searchOverrideFilters) && (
                  <Tag closable onClose={() => setStatusFilter(['all'])}>
                    Status: {statusFilter.map(s => t(`appointments.statuses.${s}`)).join(', ')}
                  </Tag>
                )}
                {dateRange && (!searchText || !searchOverrideFilters) && (
                  <Tag closable onClose={() => setDateRange(null)}>
                    Date: {dateRange[0].format('YYYY/MM/DD')} - {dateRange[1].format('YYYY/MM/DD')}
                  </Tag>
                )}
                <Button
                  type="link"
                  size="small"
                  onClick={handleClearFilters}
                  style={{ padding: 0, marginLeft: 8 }}
                >
                  {t('appointments.clearAllFilters')}
                </Button>
              </Text>
            </Col>
          </Row>
        )}
      </Card>

      {/* Appointments Table */}
      <Card>
        {/* Results Count */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary">
            {t('appointments.showingResults', {
              showing: orderedAppointments.length,
              total: orderedAppointments.length
            }) || `Showing ${orderedAppointments.length} appointments`}
          </Text>
        </div>

        <Table
          columns={columns}
          dataSource={orderedAppointments}
          loading={appointmentsLoading}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => handleViewAppointment(record),
            style: { cursor: 'pointer' }
          })}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <Text type="secondary">
                  {(searchText || (statusFilter.length > 0 && !statusFilter.includes('all')) || dateRange)
                    ? t('appointments.noResultsWithFilters') || 'No appointments found matching your filters'
                    : t('appointments.noAppointments') || 'No appointments found'
                  }
                </Text>
                {(searchText || (statusFilter.length > 0 && !statusFilter.includes('all')) || dateRange) && (
                  <div style={{ marginTop: 16 }}>
                    <Button onClick={handleClearFilters}>
                      {t('appointments.clearFilters')}
                    </Button>
                  </div>
                )}
              </div>
            )
          }}
          pagination={{
            total: orderedAppointments.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('appointments.paginationInfo', { start: range[0], end: range[1], total })
          }}
          scroll={{ x: 1620 }}
        />
      </Card>

      {/* Provider Queues - Only show for nurses */}
      {!user?.doctor_id && (
        <Card style={{ marginBottom: 24 }} title={t('appointments.providerQueues')}>
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={providerQueues}
            columns={[
              { title: t('appointments.provider'), dataIndex: 'name', key: 'name' },
              { title: t('appointments.statuses.confirmed'), dataIndex: 'confirmed', key: 'confirmed', width: 120 },
              { title: t('appointments.statuses.ready'), dataIndex: 'ready', key: 'ready', width: 120 },
              { title: t('appointments.statuses.in_progress'), dataIndex: 'in_progress', key: 'in_progress', width: 140 },
              { title: t('appointments.total'), dataIndex: 'total', key: 'total', width: 120 },
            ]}
          />
        </Card>
      )}

      {/* New Appointment Form Modal */}
      <AppointmentForm
        visible={isNewAppointmentModalVisible}
        onCancel={() => setIsNewAppointmentModalVisible(false)}
        onSuccess={handleNewAppointmentSuccess}
      />

      {assigningAppointmentId && (
        <ProviderSelectionModal
          visible={assignModalVisible}
          appointmentId={assigningAppointmentId}
          onCancel={() => setAssignModalVisible(false)}
          onAssigned={() => {
            setAssignModalVisible(false);
            setAssigningAppointmentId(null);
            refetchAppointments();
            message.success(t('appointments.assignedSuccessfully'));
          }}
        />
      )}

    </div>
  );
};

export default Appointments; 